import { IdleSessionTimeout } from "idle-session-timeout";
import {notification} from "antd";

export const SESSION_TIMEOUT_LENGTH_MS
    = 12 //hours
    * 60 //minutes
    * 60 //seconds
    * 1000; //ms

/**
 * Starts a timer for the current session that will count down while the user is idle.
 * The timer is reset by the following actions: page load, mouse move, mouse down, mouse up,
 * key press, DOM Mouse Scroll, mouse wheel, MS Pointer Move, click, scroll, touch start, touch move, touch end
 * @param timeoutLengthMs - the amount of time the session should last
 * @param onTimeout - callback that should execute when the session expires. Typically, this should be some kind of logout action.
 * @param debugLogging - Optional - enable logging
 */
export const startSession = (
    timeoutLengthMs: number,
    onTimeout: () => void,
    debugLogging?: boolean,
): void => {
    let session = new IdleSessionTimeout(timeoutLengthMs);
    let notificationShown = false;

    //Define a function to explicitly handle user activity
    const resetNotificationOnActivity = () => {
        if (notificationShown) {
            if (debugLogging) {
                console.log('User activity detected, resetting notification flag.');
            }
            notificationShown = false;
        }
    };

    // Common function to add or remove event listeners
    const updateEventListeners = (action) => {
        const eventTypes = ['mousemove', 'click', 'mousedown', 'mouseup', 'keypress', 'contextmenu', 'touchstart', 'touchmove', 'touchend', 'scroll', 'mousewheel'];
        eventTypes.forEach(event => {
            document[`${action}EventListener`](event, resetNotificationOnActivity);
        });
    };

    // Add event listeners to detect user activity
    updateEventListeners('add');

    session.onTimeOut = () => {
        // Remove event listeners when the session ends
        updateEventListeners('remove');
        onTimeout();
    };
    
    if (debugLogging) {
        console.log(`Starting session, ${timeoutLengthMs} ms`);
    }

    // Handling for session initially longer than 5 minutes
    if (timeoutLengthMs > 300000) {
        session.onTimeLeftChange = (timeLeft: number) => {
            if (debugLogging) {
                console.log(`Time left in session: ${timeLeft} ms`);
            }

            // Show the notification only if we're within the last 5 minutes and it hasn't been shown yet
            if (timeLeft <= 300000 && !notificationShown) {
                notification.warning({
                    message: 'Session Expiring Soon',
                    description: 'Your session will expire in 5 minutes due to inactivity.',
                    duration: 10, // How long the notification stays open.
                });
                notificationShown = true;
            }
        };
    } else {
        // Handling for session initially less than 5 minutes
        session.onTimeLeftChange = (timeLeft: number) => {
            if (debugLogging) {
                console.log(`Time left in session: ${timeLeft} ms`);
            }

            // Show the notification only if we're within the last 45 seconds and it hasn't been shown yet
            if (timeLeft <= 45000 && !notificationShown) {
                notification.warning({
                    message: 'Session Expiring Very Soon',
                    description: 'Your session will expire in less than 45 seconds due to inactivity.',
                    duration: 10, // Adjust duration as needed
                });
                notificationShown = true;
            }
        };
    }

    session.start();
};

/**
 * Logs the user out of their current session by creating a form that posts to the specified URL,
 * and then submitting.
 * @param lougoutEndpoint - the endpoint to which the form posts.
 */
export const logout = (
    lougoutEndpoint: string
): void => {
    const form = document.createElement("form") as HTMLFormElement;
    form.method = "POST";
    form.action = lougoutEndpoint;
    document.body.appendChild(form);
    form.submit();
};